/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    //AddToOrderTemplateComponent,
    IAddToOrderTemplateDialogResources,
    // IDuplicateItemsWhenAddingToOrderTemplateDialogResources,
    // IItemAddedToOrderTemplateDialogResources,
    // IOrderTemplateActionErrorResult,
    IOrderTemplateNameDialogResources
} from '@msdyn365-commerce/components';
import { CartLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { IEvebuyboxState, IEvebuyboxCommonResources, IEvebuyboxCallbacks } from '../utilities/eve-buyboxInterface';
import { IEveBuyboxViewProps } from '../eve-buybox';
//import { getConfigureErrors } from '../utilities/error-utilities';
import { IEveBuyboxProps } from '../eve-buybox.props.autogenerated';
import { IEveBuyboxData } from '../eve-buybox.data';
import SaveCartToOrderTemplateComponent from '../../../themes/wldtheme/views/components/savecarttoordertemplate.component';
import { GetCatalogIdFromContext } from '../../../utilities';

function getAsCartLines(
    product: SimpleProduct,
    state: IEvebuyboxState,
    props: IEveBuyboxViewProps & IEveBuyboxProps<IEveBuyboxData>
): CartLine[] {
    const retCartLines: CartLine[] = [];
    // const productRecordIds: IUnitOfMeasureRequestMap[] = [];
    // products?.forEach(product => {
    //     if (product.product?.RecordId && product.product.ItemId) {
    //         productRecordIds.push({ ProductRecordId: product.product?.RecordId, ProductItemId: product.product?.ItemId });
    //     }
    // });
    // let eachPrices: IEveEachPrice[] = [];
    const CatalogId = GetCatalogIdFromContext(props.context.actionContext);
    // await postEveUomRequestAsync({ callerContext: this.props.context.actionContext }, productRecordIds, CatalogId)
    //     .then((results: IEveEachPrice[]) => {
    //         eachPrices = results;
    //     })
    //     .catch(() => {});
    const { caseQuantity, eachQuantity } = state;
    if (caseQuantity > 0) {
        const caCartLine: CartLine = {
            ItemId: product.ItemId,
            ProductId: product.RecordId,
            Quantity: caseQuantity,
            Price: product.AdjustedPrice,
            CatalogId: CatalogId,
            UnitOfMeasureSymbol: 'Cs',
            Description: product.Description,
            IsPriceKeyedIn: false
        };
        retCartLines.push(caCartLine);
    }

    if (eachQuantity > 0) {
        const eaCartLine: CartLine = {
            ItemId: product.ItemId,
            ProductId: product.RecordId,
            Quantity: eachQuantity,
            Price: 0,
            CatalogId: CatalogId,
            UnitOfMeasureSymbol: 'Ea',
            Description: product.Description,
            IsPriceKeyedIn: false
        };
        retCartLines.push(eaCartLine);
    }
    return retCartLines;
}

/**
 * To generate AddToOrderTemplateDialog resources.
 * @param resources - The resources.
 * @returns -- Returns the list of AddToOrderTemplateDialog resources..
 */
function generateAddToOrderTemplateDialogResources(resources: IEvebuyboxCommonResources): IAddToOrderTemplateDialogResources {
    return {
        addToOrderTemplateHeader: resources.addToOrderTemplateHeader,

        noOrderTemplatesMessage: resources.noOrderTemplatesMessage,
        noOrderTemplatesDescription: resources.noOrderTemplatesDescription,

        createAnOrderTemplateButtonText: resources.createAnOrderTemplateButtonText,
        createNewOrderTemplateButtonText: resources.createNewOrderTemplateButtonText,
        cancelOrderTemplateCreationButtonText: resources.cancelOrderTemplateCreationButtonText,

        selectTemplatesText: resources.selectTemplatesText,
        addToTemplateButtonText: resources.addToTemplateButtonText,
        lineItemsText: resources.lineItemsText
    };
}

/**
 * To generate OrderTemplateNameDialog resources.
 * @param resources - The resources.
 * @returns -- Returns the list of OrderTemplateNameDialog resources..
 */
function generateOrderTemplateNameDialogResources(resources: IEvebuyboxCommonResources): IOrderTemplateNameDialogResources {
    return {
        orderTemplateHeaderLabel: resources.createOrderTemplateHeader,
        orderTemplateTitleLabel: resources.orderTemplateTitle,
        orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
        orderTemplateDescription: resources.createOrderTemplateDescription,

        defaultOrderTemplateName: resources.defaultOrderTemplateName,

        orderTemplateButtonLabel: resources.createOrderTemplateButtonText,
        orderTemplateCancelButtonLabel: resources.cancelNewOrderTemplateCreationButtonText
    };
}

/**
 * To generate ItemAddedToOrderTemplateDialog resources.
 * @param resources - The resources.
 * @returns -- Returns the list of ItemAddedToOrderTemplateDialog resources..
 */
function generateItemAddedToOrderTemplateDialogResources(resources: IEvebuyboxCommonResources) {
    return {
        viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
        continueShoppingButtonText: resources.continueShoppingButtonText,
        linesAddedToOrderTemplateHeaderItemsOneText: resources.itemAddedToOrderTemplateHeaderItemOneText,
        linesAddedToOrderTemplateHeaderItemsFormatText: resources.itemAddedToOrderTemplateHeaderItemFormatText,
        linesAddedToOrderTemplateHeaderLinesOneText: resources.itemAddedToOrderTemplateHeaderMessageText,
        linesAddedToOrderTemplateHeaderLinesFormatText: resources.itemAddedToOrderTemplateHeaderMessageText,
        linesAddedToOrderTemplateHeaderMessageText: '',
        linesAddedToOrderTemplateCustomPriceAlertMessageText: resources.originalPriceText
    };
}

/**
 * RenderAddToOrderTemplateButton.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @param product - Product data.
 * @returns -- Returns JSX element.
 */
export const renderAddToOrderTemplateButton = (
    props: IEveBuyboxViewProps & IEveBuyboxProps<IEveBuyboxData>,
    state: IEvebuyboxState,
    callbacks: IEvebuyboxCallbacks,
    product: SimpleProduct
): React.ReactNode => {
    if (props.data.orderTemplates.result && props.data.customerInformation.result) {
        return (
            <SaveCartToOrderTemplateComponent
                addToOrderTemplateDialogResources={generateAddToOrderTemplateDialogResources(props.resources)}
                createOrderTemplateDialogResources={generateOrderTemplateNameDialogResources(props.resources)}
                linesAddedToOrderTemplateDialogResources={generateItemAddedToOrderTemplateDialogResources(props.resources)}
                createOrderTemplateFromCartButton={'Add To Order Template'}
                createOrderTemplateFromCartButtonTooltip={''}
                context={props.context}
                id={props.id}
                typeName={props.typeName}
                data={{
                    orderTemplates: props.data.orderTemplates.result ?? [],
                    cartLines: getAsCartLines(product, state, props),
                    customerInformation: props.data.customerInformation.result
                }}
            />
        );
    } else {
        return <></>;
    }
};
